import { CardLayout } from 'shared/components/layouts';
import { HomeFeedItemFragment } from 'shared/graphql';
import { useIntersectionWatcher } from 'shared/hooks';

import { useStreamChatEnrichedActivity } from '../../hooks';
import { HomeFeedCardProvider, StreamActivityProvider } from '../../providers';
import { PostCardAuthor } from '../PostCardAuthor';
import { HomeFeedCardAuthor } from './HomeFeedCardAuthor';
import { HomeFeedCardContent } from './HomeFeedCardContent';
import { HomeFeedCardDiscount } from './HomeFeedCardDiscount';
import { HomeFeedCardIndicator } from './HomeFeedCardIndicator';
import { HomeFeedCardOptions } from './HomeFeedCardOptions';
import { HomeFeedCardReactions } from './HomeFeedCardReactions';

interface Props {
  data: HomeFeedItemFragment;

  /**
   * Hide the available comments for the post.
   * @default false
   */
  hideComments?: boolean;

  /**
   * Hide the available reactions for the post.
   * @default false
   */
  hideLikes?: boolean;

  /**
   * Hide the available options for the post.
   * @default false
   */
  hideOptions?: boolean;

  /**
   * Remove the border of the card.
   * @ticket https://github.com/jebelapp/jebel/issues/1525
   * @default false
   */
  borderless?: boolean;

  onClick?(): void;

  /** Handles the `onEdit` event, should be defined to show the "edit" option. */
  onEdit?(): void;
  /** Handles the `onReport` event, should be defined to show the "mark as inappropriate" option. */
  onReport?(): void;
  /** Handles the `onRemove` event, should be defined to show the "remove" option. */
  onRemove?(): void;
}

/** Shows the `HomeFeedItemFragment` as a beautiful card. */
export function HomeFeedCard(props: Props) {
  const withComments = !props.hideComments;
  const withReactions = !props.hideLikes;
  const withOptions = !props.hideOptions;

  const { ref, visible: haveBeenRender, haveBeenShown } = useIntersectionWatcher<HTMLDivElement>();

  const { data: activity, loading: activityLoading } = useStreamChatEnrichedActivity({
    skip: !haveBeenShown,
    post: props.data,
  });

  return (
    <HomeFeedCardProvider
      data={props.data}
      isHaveBeenRender={haveBeenRender}
      isHaveBeenShown={haveBeenShown}
    >
      <StreamActivityProvider data={activity} loading={activityLoading}>
        <CardLayout ref={ref} borderless={props.borderless} onClick={props.onClick}>
          <HomeFeedCardIndicator />

          <PostCardAuthor>
            <HomeFeedCardAuthor />

            {withOptions && (
              <HomeFeedCardOptions
                onEdit={props.onEdit}
                onRemove={props.onRemove}
                onReport={props.onReport}
              />
            )}
          </PostCardAuthor>

          <HomeFeedCardDiscount />
          <HomeFeedCardContent />

          <HomeFeedCardReactions withComments={withComments} withLikes={withReactions} />
        </CardLayout>
      </StreamActivityProvider>
    </HomeFeedCardProvider>
  );
}
